import { createContext, useContext } from 'react';
import type { OrderDraft, OrderService, SubmittedOrder } from '@mypoz/shared';

import type { PromoDiscount } from './api.js';

/** Where one order's detail record has got to. `idle` means nobody has asked for it yet. */
export type OrderDetailStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * How a save through `order_modify` ended (MYP-86). Each kind is a different thing to tell the
 * customer, and a different answer to "is my edit on the order now?".
 */
export type OrderModifyOutcome =
  /** The vendor accepted the edit. The order list is being re-read. */
  | { readonly kind: 'saved' }
  /**
   * The vendor refused (`status: 0`, or another code). Its reason is in `detail` — status 0 covers
   * every refusal and only the message says which. The order is re-read, because one of those
   * refusals is "a service or package failed", which may have left part of the edit applied.
   */
  | { readonly kind: 'refused'; readonly code: string; readonly detail: string }
  /**
   * The order changed on the vendor's side since the form was opened, so the edit was not applied
   * — caught before sending, or refused by the vendor's own status check. The order on screen has
   * been replaced with what the vendor has now; `current` is that order, for the form to move its
   * draft onto (`rebaseOrderDraft`).
   */
  | { readonly kind: 'stale'; readonly current: SubmittedOrder }
  /** The answer was lost after sending. The edit may or may not have landed; the order is re-read. */
  | { readonly kind: 'unconfirmed'; readonly code: string }
  /** The pre-save check could not read the order, so nothing was sent. */
  | { readonly kind: 'notSent'; readonly code: string }
  /**
   * This order carries no `modifyBase` (a record missing `ordertype`) or no vendor id.
   * Nothing can be sent for it without guessing.
   */
  | { readonly kind: 'cannotSave' };

/**
 * How a submit through `orders_create` ended (MYP-91). Each kind answers "is there an order now?"
 * differently, and is told to the customer differently.
 */
export type OrderCreateOutcome =
  /**
   * The vendor created it. `orderNumber` is the number it gave, or undefined if it said "created"
   * without one. The order list is being re-read either way.
   */
  | { readonly kind: 'created'; readonly orderNumber: string | undefined }
  /**
   * The vendor refused, with its code — 2 (billing details missing), 3–9 (one lumped line: create,
   * split, service or declared-value failure), 11 (no order type), -1/-2 (details incomplete or
   * packages not eligible). `detail` is the vendor's own reason where the document gives none.
   * No order was made.
   */
  | { readonly kind: 'refused'; readonly code: string; readonly detail: string }
  /**
   * The answer was lost after sending. The order may exist. The list has been re-read, and a
   * package that did go onto an order now carries its number, so it cannot be ordered twice.
   */
  | { readonly kind: 'unconfirmed'; readonly code: string }
  /** The draft could not be turned into a request (a step unfinished). Nothing was sent. */
  | { readonly kind: 'notSent' };

/** What asking for an order's invoice came to (MYP-124). */
export type InvoiceOutcome =
  /** A link on the vendor's site that downloads the receipt PDF. */
  | { readonly kind: 'ready'; readonly url: string }
  /** Code -2: no such order for this customer. */
  | { readonly kind: 'notFound' }
  /** Anything else: another code, no link, or no answer at all. */
  | { readonly kind: 'failed' };

/** How a payment through `orders_pay` ended (MYP-92). */
export type OrderPayOutcome =
  /** Paid. The order and the balance are the vendor's to show now; re-read them. */
  | { readonly kind: 'paid' }
  /** Code 2: the payment password was wrong. Nothing was taken. */
  | { readonly kind: 'wrongPassword' }
  /** Code 3: the wallet does not cover the order. Nothing was taken. */
  | { readonly kind: 'insufficient' }
  /** Code 0, -2 or anything undocumented. `detail` is the vendor's text. */
  | { readonly kind: 'refused'; readonly code: string; readonly detail: string }
  /**
   * The answer was lost after sending. The order was read again to find out: `nowPaid` says what it
   * shows, or is undefined if that read failed too. Never retried automatically.
   */
  | { readonly kind: 'unconfirmed'; readonly code: string; readonly nowPaid: boolean | undefined }
  /** A payment for this order is already in flight, or the order is unknown. Nothing was sent. */
  | { readonly kind: 'notSent' };

/** How checking a promo code on an order through `OrderCodenoCheck` ended (MYP-95). */
export type PromoCheckOutcome =
  /** Accepted, with the discount to send along with the payment. */
  | { readonly kind: 'valid'; readonly promo: PromoDiscount }
  /** Code -1: not valid for this order (expired, below the minimum spend, unknown…). */
  | { readonly kind: 'invalid' }
  /** The check did not answer. Nothing is applied. */
  | { readonly kind: 'failed' }
  /**
   * Not checked: the order already carries a code (a code applies once), or it has no billing
   * weight or channel to check against.
   */
  | { readonly kind: 'notAvailable' };

/** How switching an order to automatic payment through `ajaxUpdateUserOrderPayment` ended (MYP-97). */
export type OrderAutoPayOutcome =
  /** Switched. The order has been re-read, so it now shows the vendor's own payment method. */
  | { readonly kind: 'set' }
  /** The vendor refused (live: `{msg}` with no code). `detail` is its text, for the log. */
  | { readonly kind: 'refused'; readonly code: string; readonly detail: string }
  /**
   * The answer was lost after sending. The order was read again: `nowAuto` says what it shows, or is
   * undefined if that read failed too. Never retried automatically.
   */
  | { readonly kind: 'unconfirmed'; readonly nowAuto: boolean | undefined }
  /** A switch is already in flight, or the order is unknown. Nothing was sent. */
  | { readonly kind: 'notSent' };

/** How a cancel through `order_cancel` ended (MYP-94). */
export type OrderCancelOutcome =
  /** Cancelled. The order has been re-read, so the card now shows the vendor's own status. */
  | { readonly kind: 'cancelled' }
  /** Code 2, -2 or anything else the vendor refused with. `detail` is the vendor's text, for the log. */
  | { readonly kind: 'refused'; readonly code: string; readonly detail: string }
  /**
   * The answer was lost after sending. The order was read again to find out: `nowCancelled` says
   * what it shows, or is undefined if that read failed too. Never retried automatically.
   */
  | {
      readonly kind: 'unconfirmed';
      readonly code: string;
      readonly nowCancelled: boolean | undefined;
    }
  /** A cancel for this order is already in flight, or the order is unknown. Nothing was sent. */
  | { readonly kind: 'notSent' };

/** How a confirm-receipt through `order_receive` ended (MYP-93). */
export type OrderReceiveOutcome =
  /** Received. The order has been re-read, so the card now shows the vendor's own status. */
  | { readonly kind: 'received' }
  /** Code -2 or anything else the vendor refused with. `detail` is the vendor's text, for the log. */
  | { readonly kind: 'refused'; readonly code: string; readonly detail: string }
  /**
   * The answer was lost after sending. The order was read again to find out: `nowReceived` says
   * what it shows, or is undefined if that read failed too. Never retried automatically.
   */
  | {
      readonly kind: 'unconfirmed';
      readonly code: string;
      readonly nowReceived: boolean | undefined;
    }
  /** A confirm for this order is already in flight, or the order is unknown. Nothing was sent. */
  | { readonly kind: 'notSent' };

export interface OrdersState {
  /**
   * Every order on the account, newest first as `filterOrders` sorts them. Empty while loading and
   * after a failure — callers branch on `status`, not on the length of this.
   */
  readonly orders: readonly SubmittedOrder[];
  readonly status: 'loading' | 'ready' | 'error';
  /** Whether the status dictionary has loaded — it arrives before the whole list (MYP-126). */
  readonly dictionaryStatus: 'loading' | 'ready' | 'error';
  /** Summary rows from one page of `order_list`, labelled with the status dictionary (MYP-126). */
  toOrders: (rows: readonly unknown[]) => readonly SubmittedOrder[];
  /** One order's detail record — what an order-number search answers with (MYP-126). */
  toOrderFromDetail: (record: unknown) => SubmittedOrder | undefined;
  /** Goes up on every reload; the paged Orders screen asks for its page again when it changes. */
  readonly listVersion: number;
  /** Asks the vendor again. Safe to call at any time; a fetch already in flight is discarded. */
  reload: () => void;
  /**
   * Fetches one order's detail record — its packages, address and timeline, none of which a
   * summary row carries (MYP-53).
   *
   * Called when a card is expanded. Idempotent: an order already loaded, or already loading, is
   * left alone, so re-opening a card costs nothing.
   */
  loadDetail: (orderNumber: string) => void;
  /** Where `loadDetail` has got to for one order, so the panel can show a wait or a retry. */
  detailStatus: (orderNumber: string) => OrderDetailStatus;
  /**
   * Places a new order through `orders_create` (MYP-91), then re-reads the order list — the reply
   * carries only the new number, never the order. Nothing is added to the list before the vendor
   * has it.
   *
   * `warehouseId` is the one warehouse the chosen packages are in (the MYP-87 scope); `services`
   * is the catalog the chosen service ids are priced from.
   */
  submit: (
    draft: OrderDraft,
    warehouseId: string,
    services: readonly OrderService[],
    /** Uploaded purchase-receipt paths per package (MYP-96), sent as `packageinfo` when kept. */
    receiptPaths?: Readonly<Record<string, readonly string[]>>,
  ) => Promise<OrderCreateOutcome>;
  /**
   * Saves an edit to a vendor order through `order_modify` (MYP-86).
   *
   * Reads the order fresh first and sends nothing if it has moved on since the form was opened —
   * the vendor has its own status guard, but its refusal is one `status: 0` among many, and this
   * way the customer is told plainly and sees the current order. Never patches locally: the result
   * is only what the vendor says after a re-read.
   */
  modify: (orderNumber: string, draft: OrderDraft) => Promise<OrderModifyOutcome>;
  /**
   * Pays an order from the wallet through `orders_pay` (MYP-92). Changes nothing locally: the
   * order's new status comes from the vendor, via `refreshOrder` once the dialog has closed.
   */
  pay: (
    orderNumber: string,
    payPassword: string,
    promo?: PromoDiscount,
  ) => Promise<OrderPayOutcome>;
  /**
   * Checks a promo code against an unpaid order's channel and billing weight (MYP-95). Read-only.
   * Refuses to check for an order that already carries a code: a code applies once.
   */
  checkPromo: (orderNumber: string, code: string) => Promise<PromoCheckOutcome>;
  /**
   * Switches an unpaid order to automatic payment (MYP-97), then re-reads it. Cannot be undone:
   * the vendor has no way back to manual.
   */
  setAutoPay: (orderNumber: string) => Promise<OrderAutoPayOutcome>;
  /**
   * Reads one order's detail again and shows it, without clearing the list — so an open card is not
   * unmounted by a loading state. Used after a payment.
   */
  refreshOrder: (orderNumber: string) => void;
  /**
   * Cancels a collecting order through `order_cancel` (MYP-94), then re-reads that order. One
   * request at a time per session. The caller reloads the packages, since cancelling releases them.
   */
  cancel: (orderNumber: string) => Promise<OrderCancelOutcome>;
  /**
   * Confirms a shipped order received, through `order_receive` (MYP-93), then re-reads that order.
   * One request at a time per session; a second call while one is in flight sends nothing.
   */
  confirmReceipt: (orderNumber: string) => Promise<OrderReceiveOutcome>;
  /**
   * Asks for an order's invoice link (MYP-124, `get_receipt_pdf_url`). Never rejects: the outcome
   * says what to show. Unlike pay/cancel/confirmReceipt this never changes the order, so the caller
   * drives its own pending state and starts the download itself.
   */
  requestInvoice: (orderNumber: string) => Promise<InvoiceOutcome>;
}

export const OrdersContext = createContext<OrdersState | null>(null);

export function useOrders(): OrdersState {
  const value = useContext(OrdersContext);
  if (!value) throw new Error('useOrders must be used inside OrdersProvider');
  return value;
}

/** One order by number, or undefined. Kept here so pages do not each re-scan the list. */
export function useOrder(orderNumber: string | undefined): SubmittedOrder | undefined {
  const { orders } = useOrders();
  return orders.find((order) => order.orderNumber === orderNumber);
}
